import { gql } from 'graphql-request';

/**
 * POM-6223 — the Non-Routable Orders holding queue.
 *
 * THE FIELD IS `nonRoutableRequests`, NOT `nonRoutableOrders`. Parking writes no rx_request,
 * rx_order, rx_request_line_item or prescription row, so no order exists — preventing one is the
 * entire point of the feature. The tab is LABELLED "Non-Routable Orders" for operators; the wire
 * says what it holds.
 *
 * THERE IS NO `payload` FIELD AND THERE NEVER WILL BE. The parked blob is the complete original
 * request (shipping address, SIG, prescriber and patient identifiers), classified RESTRICTED, and
 * the backend deliberately does not project it. Do not add a "view request contents" selection
 * here — one page of this list is up to 200 patients at once.
 */

// The queue read.
//
// `lastError` and `hubRequestId` ARE DELIBERATELY ABSENT HERE and are still on the detail
// selection below. The queue table dropped the Last Error column, and those two were the only
// things it rendered; a selection set that outlives the column it fed is how a list read
// accumulates fields nobody can point at. Re-add them here if a column ever shows them again.
//
// `resolutionType`, `resolvedAt` AND `resolvedByPomUserId` ARE ALSO ABSENT, by the same rule.
// They were added here when a cancelled row carried a "cancelled by <user> on <date>" caption;
// the product owner removed that caption, so nothing on this screen renders them and by the rule
// above they come back out. The backend still serves all three on the queue item, and the detail
// selection below still asks for them, so a resolution view needs no schema change to show them.
const NON_ROUTABLE_QUEUE_ITEM_FIELDS = `
  nonRoutableRequestId
  referenceId
  partnerId
  partnerName
  patientId
  patientName
  productId
  drugLabel
  status
  nonRoutableReason
  nonRoutableLineCount
  attemptCount
  parkedAt
  lastUpdatedAt
  source
  relayOrderId
  relayErrorCode
  destinationName
  ageSeconds
  isStaleClaim
  availableActions
  relayOrdersMayExist
`;

// POM-6279 — the last eight fields above.
//
// `availableActions` IS WHAT THE ROW MENU RENDERS, and the portal never works it out: the server
// decides per row from source, reason, status, relay error code and stale claim, and refuses
// anything else with NON_ROUTABLE_REQUEST_ACTION_NOT_ALLOWED. `relayErrorCode` is a String on
// purpose (an unknown value must not take the page to data:null). `ageSeconds` is measured on the
// database clock.
// `relayOrdersMayExist` is read from the relay orders themselves, never from the reason.

// The by-id read and the mutations. Same fields PLUS the full lifecycle status.
//
// IT IS NO LONGER "the only way to see a row after it has left the queue" — a CANCELLED row is
// listable from the queue itself now. It IS still the only way to reach a RESOLVED one, which the
// queue read cannot return at all.
const NON_ROUTABLE_DETAIL_FIELDS = `
  nonRoutableRequestId
  referenceId
  partnerId
  patientId
  status
  nonRoutableReason
  nonRoutableLineCount
  attemptCount
  lastError
  hubRequestId
  resolutionType
  resolvedAt
  resolvedByPomUserId
  parkedAt
  lastUpdatedAt
  source
  relayOrderId
  isStaleClaim
  availableActions
  relayOrdersMayExist
`;

export const listNonRoutableRequestsGql = gql`
  query nonRoutableRequests(
    $filter: NonRoutableRequestsFilter
    $sortBy: NonRoutableRequestsSortBy
    $limit: Int
    $offset: Int
  ) {
    nonRoutableRequests(filter: $filter, sortBy: $sortBy, limit: $limit, offset: $offset) {
      items {
        ${NON_ROUTABLE_QUEUE_ITEM_FIELDS}
      }
      totalCount
      limit
      offset
    }
  }
`;

// NULLABLE ON PURPOSE on the backend: a primary-key miss is a legitimate answer, not an error.
// The caller must treat `null` as "no request carries that id", never as a failure.
export const getNonRoutableRequestGql = gql`
  query nonRoutableRequest($nonRoutableRequestId: ID!) {
    nonRoutableRequest(nonRoutableRequestId: $nonRoutableRequestId) {
      ${NON_ROUTABLE_DETAIL_FIELDS}
    }
  }
`;

// BRING BACK: this turns a held prescription into an order that WILL BE DISPENSED, and there is
// no undo. Non-null on the backend — it either released the request and returns the row as the
// database now has it, or it raises a typed error. There is no third answer.
export const bringBackNonRoutableRequestToPomGql = gql`
  mutation bringBackNonRoutableRequestToPom($nonRoutableRequestId: ID!) {
    bringBackNonRoutableRequestToPom(nonRoutableRequestId: $nonRoutableRequestId) {
      ${NON_ROUTABLE_DETAIL_FIELDS}
    }
  }
`;

export const moveRelayFaxOrderToPomGql = gql`
  mutation moveRelayFaxOrderToPom($nonRoutableRequestId: ID!) {
    moveRelayFaxOrderToPom(nonRoutableRequestId: $nonRoutableRequestId) {
      ${NON_ROUTABLE_DETAIL_FIELDS}
    }
  }
`;

// CANCEL: terminal, and the opposite decision. The prescription will never be filled, no POM
// order is created and no partner is told.
export const cancelNonRoutableRequestGql = gql`
  mutation cancelNonRoutableRequest($nonRoutableRequestId: ID!) {
    cancelNonRoutableRequest(nonRoutableRequestId: $nonRoutableRequestId) {
      ${NON_ROUTABLE_DETAIL_FIELDS}
    }
  }
`;

// RETRY ROUTING: re-runs the ENTIRE routing workflow from the first step against the CURRENT
// configuration. It does not replay the stored decision — the backend re-invokes the Routing Hub
// and fails closed if the hub is unreachable or answers un-enriched, leaving the request parked
// rather than replaying a stale outcome and reporting success. On a fresh destination the order
// continues into that destination's normal flow; on the same failure it re-parks with an
// incremented attempt count.
export const retryNonRoutableRequestRoutingGql = gql`
  mutation retryNonRoutableRequestRouting($nonRoutableRequestId: ID!) {
    retryNonRoutableRequestRouting(nonRoutableRequestId: $nonRoutableRequestId) {
      ${NON_ROUTABLE_DETAIL_FIELDS}
    }
  }
`;

// ---- POM-6279: parked RELAY FAX -------------------------------------------------------------

// What every fax mutation returns: the row as the database now has it, what happened, and —
// CANCEL ONLY — whether the fax may already be at the pharmacy (the one-send claim was held).
const RELAY_FAX_ACTION_RESULT_FIELDS = `
  request {
    ${NON_ROUTABLE_DETAIL_FIELDS}
  }
  outcome
  faxMayHaveBeenSent
`;

// One parked relay fax in full. Nullable: no row, or a row that is not a fax park, is "nothing
// here", not an error. NO payload, NO prescription_data, NO ship-to address and NO document keys
// exist on this type — the line items are scalars only, under relayOrderDetails' own PHI rules.
export const getNonRoutableRelayFaxDetailGql = gql`
  query nonRoutableRelayFaxDetail($nonRoutableRequestId: ID!) {
    nonRoutableRelayFaxDetail(nonRoutableRequestId: $nonRoutableRequestId) {
      request {
        ${NON_ROUTABLE_DETAIL_FIELDS}
      }
      relay {
        relayOrderId
        externalOrderId
        relayOrderStatus
        destinationName
        destinationEndpoint
        transmissionReference
        relayErrorCode
        relayErrorDetail
        isClaimHeld
        attemptCount
        lastAttemptAt
        dlqAt
      }
      lineItems {
        relayLineItemId
        ordinal
        drugName
        ndc
        quantity
        vendorScriptNumber
      }
      events {
        eventId
        eventType
        actor
        pomUserId
        httpStatus
        vendorReference
        eventData
        createdTimestamp
      }
      hasMoreEvents
    }
  }
`;

// RESEND. A FAX CANNOT BE RECALLED. When the last send's outcome is unknown the server refuses with
// RELAY_FAX_RESEND_CONFIRMATION_REQUIRED unless `confirmResend` is true — the operator phoned the
// pharmacy and it did NOT receive the fax.
export const resendRelayFaxGql = gql`
  mutation resendRelayFax($nonRoutableRequestId: ID!, $confirmResend: Boolean) {
    resendRelayFax(nonRoutableRequestId: $nonRoutableRequestId, confirmResend: $confirmResend) {
      ${RELAY_FAX_ACTION_RESULT_FIELDS}
    }
  }
`;

// RECONCILE: the operator confirmed BY PHONE that the pharmacy has the fax. The relay order moves
// to TRANSFERRED and the row resolves RECONCILED.
export const reconcileRelayFaxGql = gql`
  mutation reconcileRelayFax($nonRoutableRequestId: ID!) {
    reconcileRelayFax(nonRoutableRequestId: $nonRoutableRequestId) {
      ${RELAY_FAX_ACTION_RESULT_FIELDS}
    }
  }
`;

// CANCEL FAX: the relay order AND the row are cancelled together. No partner is notified.
export const cancelRelayFaxGql = gql`
  mutation cancelRelayFax($nonRoutableRequestId: ID!) {
    cancelRelayFax(nonRoutableRequestId: $nonRoutableRequestId) {
      ${RELAY_FAX_ACTION_RESULT_FIELDS}
    }
  }
`;
